import React, { FC, ReactNode } from 'react';

import { DropdownPortal, Tooltip } from 'common/components';
import DropdownItem from 'common/components/base/dropdown-portal/DropdownPortalItem';
import Icon from 'common/components/base/icon';
import { EntityType } from 'common/enums';
import { ADD_DISABLED_IN_REVIEW_TOOLTIP, LINKED_INDICATOR_EDIT_TOOLTIP } from 'common/config';
import { EntityActions } from 'common/enums/actions.enum';
import { IconType } from 'common/enums/controls/dropdown.enum';
import { ICON_COLLECTION } from 'assets/media/icons';

import { KebabOption } from 'modules/master-data/models/master-data.model';
import {
    CategoryKebabOptions,
    IndicatorKebabOptions,
    SubCategoryKebabOptions
} from 'modules/master-data/config';

import './KebabOptions.scss';
import { UseMasterDataAccess } from 'modules/master-data/role-access-hooks/useMasterDataAccess';
import { Mode } from 'modules/data-editor/enums/mode.enum';

type KebabOptionsProps = {
    data: any;
    isUploadDisabled: boolean;
    uploadedUser: string;
    masterDataFeatureActionList?: UseMasterDataAccess;
    isSyncInProgress: boolean;
    onAction: (action: string, data: any) => void;
};

const KebabOptions: FC<KebabOptionsProps> = ({
    data,
    isUploadDisabled,
    uploadedUser,
    masterDataFeatureActionList,
    isSyncInProgress,
    onAction
}: KebabOptionsProps) => {
    let options: KebabOption[] = [];

    const CategoryFeatureAccess = masterDataFeatureActionList?.categoryFeatureAccess;
    const SubCategoryFeatureAccess = masterDataFeatureActionList?.subCategoryFeatureAccess;
    const IndicatorFeatureAccess = masterDataFeatureActionList?.indicatorFeatureAccess;

    switch (data.entityType) {
        case EntityType.Category: {
            //Update isActionDisabled in CategoryKebabOptions with FeatureAccess from CategoryFeatureAccess
            const updatedCategoryKebabOptions = CategoryKebabOptions.map((option) => {
                let isActionDisabled: boolean | undefined = false; // Default value

                switch (option.label) {
                    case EntityActions.AddNewSubCategory:
                        isActionDisabled = CategoryFeatureAccess?.isAddNewSubCategoryDisabled;
                        break;
                    case EntityActions.Edit:
                        isActionDisabled = CategoryFeatureAccess?.isEditDisabled;
                        break;
                    case EntityActions.Delete:
                        isActionDisabled = CategoryFeatureAccess?.isDeleteDisabled;
                        break;
                    case EntityActions.Archive:
                        isActionDisabled = CategoryFeatureAccess?.isArchiveDisabled;
                        break;
                    case EntityActions.Clone:
                        isActionDisabled = CategoryFeatureAccess?.isCloneDisabled;
                        break;
                    case EntityActions.Export:
                        isActionDisabled =
                            CategoryFeatureAccess?.isExportCategoryDisabled || isSyncInProgress;
                        break;
                }

                return {
                    ...option,
                    isActionDisabled: isActionDisabled
                };
            });

            options = [...updatedCategoryKebabOptions];
            break;
        }
        case EntityType.SubCategory: {
            const updatedSubCategoryKebabOptions = SubCategoryKebabOptions.map((option) => {
                let isActionDisabled: boolean | undefined = false; // Default value

                switch (option.label) {
                    case EntityActions.AddNewSubCategory:
                        isActionDisabled = SubCategoryFeatureAccess?.isAddNewSubCategoryDisabled;
                        break;
                    case EntityActions.AddNewIndicator:
                        isActionDisabled = SubCategoryFeatureAccess?.isAddNewIndicatorDisabled;
                        break;
                    case EntityActions.Edit:
                        isActionDisabled = SubCategoryFeatureAccess?.isEditDisabled;
                        break;
                    case EntityActions.Delete:
                        isActionDisabled = SubCategoryFeatureAccess?.isDeleteDisabled;
                        break;
                    case EntityActions.Archive:
                        isActionDisabled = SubCategoryFeatureAccess?.isArchiveDisabled;
                        break;
                    case EntityActions.Clone:
                        isActionDisabled = SubCategoryFeatureAccess?.isCloneDisabled;
                        break;
                    case EntityActions.Move:
                        isActionDisabled = SubCategoryFeatureAccess?.isMoveDisabled;
                        break;
                    case EntityActions.Export:
                        isActionDisabled =
                            SubCategoryFeatureAccess?.isExportSubCategoryDisabled ||
                            isSyncInProgress;
                        break;
                }

                return {
                    ...option,
                    isActionDisabled: isActionDisabled
                };
            });

            options = [...updatedSubCategoryKebabOptions];

            const addIndicatorIndex = options.findIndex(
                (opt) => opt.label === EntityActions.AddNewIndicator
            );
            if (data.hasChild && data.hasChildIndicatorsOnly) {
                if (isUploadDisabled) {
                    options[addIndicatorIndex]['disable'] = true;
                    options[addIndicatorIndex][
                        'tooltipText'
                    ] = `Upload is in progress, locked by ${uploadedUser}`;
                } else if (!data.fromMaster) {
                    options[addIndicatorIndex]['disable'] = true;
                    options[addIndicatorIndex]['tooltipText'] = ADD_DISABLED_IN_REVIEW_TOOLTIP;
                } else {
                    options[addIndicatorIndex]['disable'] = false;
                    options[addIndicatorIndex]['tooltipText'] = '';
                }
            } else if (!data.hasChild) {
                if (isUploadDisabled) {
                    options[addIndicatorIndex]['disable'] = true;
                    options[addIndicatorIndex][
                        'tooltipText'
                    ] = `Upload is in progress, locked by ${uploadedUser}`;
                } else if (!data.fromMaster) {
                    options[addIndicatorIndex]['disable'] = true;
                    options[addIndicatorIndex]['tooltipText'] = ADD_DISABLED_IN_REVIEW_TOOLTIP;
                } else {
                    options[addIndicatorIndex]['disable'] = false;
                    options[addIndicatorIndex]['tooltipText'] = '';
                }
            } else if (data.hasChild && !data.hasChildIndicatorsOnly) {
                options.splice(addIndicatorIndex, 1);
            }
            if (data.hasChildIndicatorsOnly) {
                const newSubCategoryIndex = options.findIndex(
                    (opt) => opt.label === EntityActions.AddNewSubCategory
                );
                options.splice(newSubCategoryIndex, 1);
            }
            break;
        }
        case EntityType.Indicator: {
            const updatedIndicatorKebabOptions = IndicatorKebabOptions.map((option) => {
                let isActionDisabled: boolean | undefined = false; // Default value

                switch (option.label) {
                    case EntityActions.Edit:
                        isActionDisabled = IndicatorFeatureAccess?.isEditDisabled;
                        break;
                    case EntityActions.Delete:
                        isActionDisabled = IndicatorFeatureAccess?.isDeleteDisabled;
                        break;
                    case EntityActions.Archive:
                        isActionDisabled = IndicatorFeatureAccess?.isArchiveDisabled;
                        break;
                    case EntityActions.Move:
                        isActionDisabled = IndicatorFeatureAccess?.isMoveDisabled;
                        break;
                    case EntityActions.Copy:
                        isActionDisabled = IndicatorFeatureAccess?.isCopyDisabled;
                        break;
                    case EntityActions.Link:
                        isActionDisabled = IndicatorFeatureAccess?.isLinkDisabled;
                        break;
                    case EntityActions.Export:
                        isActionDisabled =
                            IndicatorFeatureAccess?.isExportIndicatorDisabled || isSyncInProgress;
                        break;
                }

                return {
                    ...option,
                    isActionDisabled: isActionDisabled
                };
            });

            options = [...updatedIndicatorKebabOptions];

            if (data.isChildLink === true) {
                options.forEach((opt) => {
                    switch (opt.label) {
                        case EntityActions.Link:
                            opt.disable = true;
                            break;
                        case EntityActions.Archive:
                            opt.disable = true;
                            break;
                        case EntityActions.Delete:
                            opt.disable = false;
                            break;
                        case EntityActions.Edit:
                            opt.disable = true;
                            opt.tooltipText = LINKED_INDICATOR_EDIT_TOOLTIP;
                            break;
                    }
                });
            } else if (data.isChildLink === false) {
                options.forEach((opt) => {
                    switch (opt.label) {
                        case EntityActions.Link:
                            opt.disable = false;
                            break;
                        case EntityActions.Archive:
                            opt.disable = false;
                            break;
                        case EntityActions.Delete:
                            opt.disable = true;
                            break;
                        case EntityActions.Edit:
                            opt.disable = false;
                            break;
                    }
                });
            } else {
                options.forEach((opt) => {
                    switch (opt.label) {
                        case EntityActions.Link:
                            opt.disable = false;
                            break;
                        case EntityActions.Archive:
                            opt.disable = false;
                            break;
                        case EntityActions.Delete:
                            opt.disable = true;
                            break;
                        case EntityActions.Edit:
                            opt.disable = false;
                            break;
                    }
                });
            }

            break;
        }
    }

    const getDropdownLabel = (option: KebabOption) => {
        if (option.tooltipText) {
            return (
                <span
                    className={`kebab-option-with-tooltip ${
                        option.disable ? 'disabled' : 'default'
                    }`}>
                    {option.label}
                    {option.disable && (
                        <Tooltip
                            trigger={
                                <Icon
                                    src={ICON_COLLECTION.IC_Help_Outline}
                                    className="tooltip-help-icon"
                                />
                            }
                            placement="right"
                            hideCloseButton
                            variant="alt"
                            hideArrow={true}
                            allowClick={false}>
                            {option.tooltipText}
                        </Tooltip>
                    )}
                </span>
            );
        }
        return option.label;
    };

    const getDropDownItem = (option: KebabOption, index: number): ReactNode => (
        <DropdownItem
            action={option.label}
            key={index}
            disabled={option.isActionDisabled || option.disable}
            onClickHandler={(action: string): void => onAction(action, data)}>
            {option.iconType === IconType.Icon ? (
                <Icon
                    className={`${
                        option.isActionDisabled || option.disable
                            ? 'disable-image-icon'
                            : 'image-icon'
                    }`}
                    src={option.iconSrc}
                />
            ) : (
                <img
                    className={`${
                        option.isActionDisabled || option.disable
                            ? 'disable-image-icon'
                            : 'image-icon'
                    }`}
                    src={option.iconSrc}
                />
            )}
            {getDropdownLabel(option)}
        </DropdownItem>
    );

    const uniqueId =
        data.entityType === EntityType.Category || data.entityType === EntityType.Indicator
            ? data.id
            : data.hierarchyId;

    return (
        <DropdownPortal id={''} triggerId={uniqueId} className="kebab-options">
            {options.map((opt, index) => getDropDownItem(opt, index))}
        </DropdownPortal>
    );
};

export default KebabOptions;
